import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Button, CellText, Copy01, DotsVertical, Edit01, Menu, Plus, Table, Trash01 } from "@opendate-app/setlist";
import PromoCodeDrawer from "./PromoCodeDrawer";
import PromoCodeTypeBadges, { promoCodeAppliesDiscount } from "../../ConfirmTicketing/components/PromoCodeTypeBadges";
import { blankPromoCode } from "../scheduleBuilder";

export const discountLabel = (code) =>
  code.discount === "" ? "—"
  : code.discountType === "amount" ? `$${Number(code.discount || 0).toFixed(2)}` : `${Number(code.discount || 0)}%`;

const newKeys = () => {
  const { id, clientKey } = blankPromoCode();
  return { id, clientKey };
};

const windowLabel = (code) => (code.startActivity ? `${code.startActivity} → ${code.endActivity}` : "Public on-sale → last entry");

const PromoCodesTab = ({ promoCodes, ticketTypeNames, holdNames = [], showActivityNames, onChange }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [copying, setCopying] = useState(null);
  const editing = promoCodes.find((code) => code.id === editingId) || null;
  const initial = useMemo(() => (editing && { ...editing }) || copying, [editing, copying]);
  const takenNames = promoCodes.filter((code) => code.id !== editingId).map((code) => code.name);

  const close = () => {
    setDrawerOpen(false);
    setEditingId(null);
    setCopying(null);
  };

  const save = (draft) => {
    onChange(
      editing
        ? promoCodes.map((code) => (code.id === editing.id ? { ...draft, id: code.id } : code))
        : [...promoCodes, { ...draft, ...newKeys() }],
    );
    close();
  };

  // A copy keeps every setting but needs a code of its own, as on a confirm.
  const copy = (code) => {
    setCopying({ ...code, ...newKeys(), name: "", shouldBeDefault: false, templateId: null });
    setDrawerOpen(true);
  };

  return (
    <div className="series-tab-panel">
      <div className="series-ticket-toolbar">
        <p className="series-edit-hint">
          Create promo codes that apply a discount at checkout. Every date of this show type gets its own copy.
        </p>
        <div className="series-ticket-toolbar-actions">
          <Button variant="secondary" icon={Plus} onClick={() => setDrawerOpen(true)}>
            Add Promo Code
          </Button>
        </div>
      </div>

      <Table
        columns={[
          { key: "name", header: "Name", primary: true, render: (code) => <CellText primary={code.name} /> },
          {
            key: "type",
            header: "Code Type",
            render: (code) => <PromoCodeTypeBadges reveals={code.revealHiddenTickets} discounts={promoCodeAppliesDiscount({ revealsHiddenTickets: code.revealHiddenTickets, percentage: code.discount, amount: code.discount })} />,
          },
          { key: "discount", header: "Discount", render: discountLabel },
          { key: "uses", header: "Uses", render: (code) => (code.unlimited ? "Unlimited" : `${code.limit} per date`) },
          { key: "window", header: "Available", render: windowLabel },
          {
            key: "actions",
            header: "",
            align: "right",
            width: "56px",
            render: (code) => (
              <Menu
                placement="bottom-end"
                minWidth={200}
                items={[
                  { label: "Edit", icon: Edit01, onClick: () => { setEditingId(code.id); setDrawerOpen(true); } },
                  { label: "Copy", icon: Copy01, onClick: () => copy(code) },
                  { divider: true },
                  { label: "Delete", icon: Trash01, destructive: true, onClick: () => onChange(promoCodes.filter((candidate) => candidate.id !== code.id)) },
                ]}
                trigger={<Button variant="tertiary" size="sm" icon={DotsVertical} aria-label={`Actions for ${code.name}`} />}
              />
            ),
          },
        ]}
        data={promoCodes}
        getRowKey={(code) => code.id}
        empty="No promo codes yet."
      />

      <PromoCodeDrawer
        open={drawerOpen}
        initial={initial}
        editing={Boolean(editing)}
        takenNames={takenNames}
        ticketTypeNames={ticketTypeNames}
        holdNames={holdNames}
        showActivityNames={showActivityNames}
        onCancel={close}
        onSave={save}
      />
    </div>
  );
};

PromoCodesTab.propTypes = {
  promoCodes: PropTypes.arrayOf(PropTypes.object).isRequired,
  ticketTypeNames: PropTypes.arrayOf(PropTypes.string).isRequired,
  holdNames: PropTypes.arrayOf(PropTypes.string),
  showActivityNames: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default PromoCodesTab;
